import React, { useEffect, useMemo, useState } from "react";
import {
    Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from "reactstrap";
import { Loading } from "../utils/Loading";
import { Pager } from "../utils/Pager";
import { downloadFileFromBase64Array } from "../utils/ValuesFormatter";
import {
    LogCarpetaEliminarResultado,
    LogCarpetaListItem,
    LogFicheroListItem,
    LogProveedorContenido,
    LogsProveedoresListado,
    LogsProveedoresPurgaResultado,
    LogsProveedoresService,
    TipoLogProveedor
} from "./LogsProveedoresService";

/** Columnas por las que se puede ordenar la tabla de ficheros. */
type ColumnaOrden = "nombre" | "tipo" | "fecha" | "tamanno";

/**
 * Explorador de los logs de las llamadas a las editoriales: por cada acceso de un alumno a un contenido
 * digital se guardan en el servidor la petición enviada y la respuesta recibida, en un árbol
 * Editorial / año / mes / día.
 *
 * Solo la ve un AdminGlobal: la ruta la protege IntranetRouter y el backend lo vuelve a comprobar, que es
 * donde de verdad cuenta.
 */
export const Index: React.FC = () => {

    const [listado, setListado] = useState<LogsProveedoresListado | null>(null);
    const [ruta, setRuta] = useState<string>("");
    const [cargando, setCargando] = useState<boolean>(true);
    const [ficheroVisto, setFicheroVisto] = useState<LogProveedorContenido | null>(null);
    const [recargas, setRecargas] = useState<number>(0);

    // Orden y paginación de la tabla de ficheros. Se hacen en cliente: el backend ya acota la carpeta a
    // sus ficheros más recientes, así que aquí siempre hay una lista manejable.
    const [orden, setOrden] = useState<ColumnaOrden>("fecha");
    const [ordenAscendente, setOrdenAscendente] = useState<boolean>(false);
    const [pagina, setPagina] = useState<number>(0);
    const [ficherosPorPagina, setFicherosPorPagina] = useState<number>(20);

    // Purga
    const [modalPurgaAbierto, setModalPurgaAbierto] = useState<boolean>(false);
    const [fechaCorte, setFechaCorte] = useState<string>("");
    const [purgarSoloEstaCarpeta, setPurgarSoloEstaCarpeta] = useState<boolean>(false);
    const [simulacion, setSimulacion] = useState<LogsProveedoresPurgaResultado | null>(null);
    const [resultadoPurga, setResultadoPurga] = useState<LogsProveedoresPurgaResultado | null>(null);
    const [borradoAsumido, setBorradoAsumido] = useState<boolean>(false);

    // Borrado de una carpeta completa. Es el camino rápido frente a la purga por fecha: el árbol está
    // agrupado por editorial, año, mes y día, así que tirar un mes entero es borrar una carpeta.
    const [carpetaABorrar, setCarpetaABorrar] = useState<LogCarpetaListItem | null>(null);
    const [resultadoCarpeta, setResultadoCarpeta] = useState<LogCarpetaEliminarResultado | null>(null);
    // Recuento de lo que se va a borrar, para que la confirmación muestre el alcance igual que la purga.
    const [contenidoCarpeta, setContenidoCarpeta] = useState<LogCarpetaEliminarResultado | null>(null);

    useEffect(() => {
        let cancelado = false;

        setCargando(true);
        new LogsProveedoresService().listar(ruta)
            .then(resultado => {
                if (!cancelado) {
                    setListado(resultado);
                    setCargando(false);
                }
            }, () => {
                // El banner de error lo pinta ApiFetcher; aquí solo hay que soltar el indicador de carga.
                if (!cancelado) {
                    setCargando(false);
                }
            });

        return () => { cancelado = true; };
    }, [ruta, recargas]);

    const navegarA = (nuevaRuta: string) => {
        setFicheroVisto(null);
        setPagina(0);
        // El resumen del último borrado habla de la carpeta anterior: dejarlo puesto al cambiar de carpeta
        // hace creer que describe la que se está viendo.
        setResultadoCarpeta(null);
        setRuta(nuevaRuta || "");
    };

    const ordenarPor = (columna: ColumnaOrden) => {
        // Repetir columna invierte el sentido; cambiar de columna empieza por el orden más útil de cada
        // una: descendente en fecha y tamaño (lo más reciente y lo más grande arriba), ascendente en texto.
        if (columna === orden) {
            setOrdenAscendente(!ordenAscendente);
        } else {
            setOrden(columna);
            setOrdenAscendente(columna === "nombre" || columna === "tipo");
        }
        setPagina(0);
    };

    const ficherosOrdenados = useMemo(() => {
        const ficheros = [...(listado?.ficheros || [])];
        const signo = ordenAscendente ? 1 : -1;

        ficheros.sort((a, b) => {
            switch (orden) {
                case "nombre":
                    return signo * a.nombre.localeCompare(b.nombre);
                case "tipo":
                    return signo * a.tipo.localeCompare(b.tipo);
                case "tamanno":
                    return signo * (a.tamanno - b.tamanno);
                default:
                    return signo * (a.fechaModificacion.getTime() - b.fechaModificacion.getTime());
            }
        });

        return ficheros;
    }, [listado, orden, ordenAscendente]);

    const ficherosDeLaPagina = ficherosOrdenados.slice(
        pagina * ficherosPorPagina, (pagina + 1) * ficherosPorPagina);

    const verFichero = (rutaFichero: string) => {
        setCargando(true);
        new LogsProveedoresService().ver(rutaFichero)
            .then(contenido => {
                setFicheroVisto(contenido);
                setCargando(false);
            }, () => setCargando(false));
    };

    const descargarFichero = (rutaFichero: string) => {
        setCargando(true);
        new LogsProveedoresService().descargar(rutaFichero)
            .then(resultado => {
                downloadFileFromBase64Array(resultado.content, resultado.fileName, "text/plain");
                setCargando(false);
            }, () => setCargando(false));
    };

    // ── Purga ────────────────────────────────────────────────────────────────

    const abrirPurga = () => {
        setSimulacion(null);
        setResultadoPurga(null);
        setBorradoAsumido(false);
        setPurgarSoloEstaCarpeta(false);
        setModalPurgaAbierto(true);
    };

    /** Carpeta sobre la que actúa la purga: la actual o todo el árbol. */
    const rutaAPurgar = () => purgarSoloEstaCarpeta ? (listado?.ruta || "") : "";

    /** Cuenta lo que se borraría, sin borrar nada. */
    const simularPurga = () => {
        setCargando(true);
        setResultadoPurga(null);
        setBorradoAsumido(false);
        new LogsProveedoresService().purgar(fechaCorte, rutaAPurgar(), true)
            .then(resultado => {
                setSimulacion(resultado);
                setCargando(false);
            }, () => setCargando(false));
    };

    /** Borra de verdad. Solo se llega aquí tras comprobar y marcar que se asume el borrado. */
    const purgar = () => {
        setCargando(true);
        new LogsProveedoresService().purgar(fechaCorte, rutaAPurgar(), false)
            .then(resultado => {
                setResultadoPurga(resultado);
                setSimulacion(null);
                setBorradoAsumido(false);
                setFicheroVisto(null);
                setCargando(false);
                setPagina(0);
                // El árbol ha cambiado bajo los pies: hay que releer la carpeta que se está viendo.
                setRecargas(n => n + 1);
            }, () => setCargando(false));
    };

    // ── Borrado de una carpeta completa ──────────────────────────────────────

    /**
     * Abre la confirmación y, de paso, cuenta lo que hay dentro: el borrado de una carpeta no se puede
     * deshacer y un clic en el icono de al lado del enlace de navegación se lleva la editorial entera, así
     * que conviene ver el alcance antes de aceptar.
     */
    const pedirBorradoCarpeta = (carpeta: LogCarpetaListItem) => {
        setResultadoCarpeta(null);
        setContenidoCarpeta(null);
        setCarpetaABorrar(carpeta);

        new LogsProveedoresService().eliminarCarpeta(carpeta.ruta, true)
            .then(resultado => setContenidoCarpeta(resultado), () => { /* el error lo pinta ApiFetcher */ });
    };

    /** Borra la carpeta confirmada con todo su contenido y recarga el listado. */
    const borrarCarpeta = () => {
        const carpeta = carpetaABorrar;

        if (carpeta == null) {
            return;
        }

        setCarpetaABorrar(null);
        setContenidoCarpeta(null);
        setCargando(true);
        new LogsProveedoresService().eliminarCarpeta(carpeta.ruta, false)
            .then(resultado => {
                setResultadoCarpeta(resultado);
                setFicheroVisto(null);
                setPagina(0);
                setCargando(false);
                // La carpeta que se está viendo acaba de perder una hija: hay que releerla.
                setRecargas(n => n + 1);
            }, () => setCargando(false));
    };

    // ── Formateo ─────────────────────────────────────────────────────────────

    const formatearTamanno = (bytes: number) =>
        bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`;

    const formatearTamannoTotal = (bytes: number) =>
        bytes < 1024 * 1024
            ? `${Math.round(bytes / 1024)} KB`
            : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

    const renderTipo = (tipo: TipoLogProveedor) => {
        if (tipo === TipoLogProveedor.Peticion) {
            return <span title="Lo que se envió a la editorial">Petición</span>;
        }
        if (tipo === TipoLogProveedor.Respuesta) {
            return <span title="Lo que devolvió la editorial">Respuesta</span>;
        }
        return <span>-</span>;
    };

    /** Cabecera de columna ordenable, con la flecha del sentido activo. */
    const renderCabecera = (columna: ColumnaOrden, titulo: string, alineadaDerecha?: boolean) =>
        <th className={`pointer${alineadaDerecha ? " text-end" : ""}`} onClick={() => ordenarPor(columna)}>
            {titulo}
            {orden === columna && <span> {ordenAscendente ? "▲" : "▼"}</span>}
        </th>;

    // ── Render ───────────────────────────────────────────────────────────────

    /** Migas de la ruta actual, para poder volver a cualquier nivel de un clic. */
    const renderMigas = () => {
        const segmentos = (listado?.ruta || "").split("\\").filter(s => s.length > 0);

        return <div>
            <a href="#" onClick={(e) => { e.preventDefault(); navegarA(""); }}>Todas las editoriales</a>
            {segmentos.map((segmento, i) => {
                const rutaSegmento = segmentos.slice(0, i + 1).join("\\");
                return <span key={rutaSegmento}>
                    {" / "}
                    {i === segmentos.length - 1
                        ? <strong>{segmento}</strong>
                        : <a href="#" onClick={(e) => { e.preventDefault(); navegarA(rutaSegmento); }}>{segmento}</a>}
                </span>;
            })}
        </div>;
    };

    const renderResultadoPurga = (resultado: LogsProveedoresPurgaResultado, esSimulacion: boolean) => {
        if (resultado.aviso) {
            return <div className="alert alert-warning">{resultado.aviso}</div>;
        }

        return <div className={esSimulacion ? "alert alert-info" : "alert alert-success"}>
            {esSimulacion
                ? <>Se borrarían <strong>{resultado.ficherosEliminados}</strong> ficheros
                    ({formatearTamannoTotal(resultado.bytesLiberados)}).</>
                : <>Borrados <strong>{resultado.ficherosEliminados}</strong> ficheros
                    ({formatearTamannoTotal(resultado.bytesLiberados)})
                    {resultado.carpetasEliminadas > 0 && <> y {resultado.carpetasEliminadas} carpetas vacías</>}.</>}
            {resultado.errores.length > 0 && <>
                <div className="mt-2">No se han podido borrar {resultado.errores.length}:</div>
                <ul className="mb-0">
                    {resultado.errores.slice(0, 10).map((error, i) => <li key={i}>{error}</li>)}
                </ul>
            </>}
        </div>;
    };

    /** Confirmación del borrado de una carpeta: se lleva todo lo que cuelga de ella y no se deshace. */
    const renderModalCarpeta = () => <Modal isOpen={true}>
        <ModalHeader close={<button className="btn-close" onClick={() => setCarpetaABorrar(null)}>&nbsp;</button>}>
            Borrar la carpeta
        </ModalHeader>

        <ModalBody>
            <p>
                Se va a borrar <strong>{carpetaABorrar?.ruta}</strong> con <strong>todos</strong> los logs y
                subcarpetas que contiene.
            </p>

            {contenidoCarpeta == null
                ? <p className="text-muted">Contando lo que hay dentro…</p>
                : contenidoCarpeta.aviso
                    ? <div className="alert alert-warning">{contenidoCarpeta.aviso}</div>
                    : <div className="alert alert-info">
                        Contiene <strong>{contenidoCarpeta.ficherosEliminados}</strong> ficheros
                        {" "}({formatearTamannoTotal(contenidoCarpeta.bytesLiberados)})
                        {contenidoCarpeta.carpetasEliminadas > 1 &&
                            <> en <strong>{contenidoCarpeta.carpetasEliminadas - 1}</strong> subcarpetas</>}.
                    </div>}

            <p className="mb-0 text-muted">
                No se puede deshacer. A diferencia de la purga por fecha, aquí no se mira la antigüedad de
                cada fichero: se va la carpeta entera.
            </p>
        </ModalBody>

        <ModalFooter>
            <Button color="danger" onClick={borrarCarpeta}>
                Borrar la carpeta
            </Button>
            <Button color="link" onClick={() => setCarpetaABorrar(null)}>
                Cancelar
            </Button>
        </ModalFooter>
    </Modal>;

    const renderModalPurga = () => {
        const puedeBorrar = simulacion != null
            && !simulacion.aviso
            && simulacion.ficherosEliminados > 0
            && borradoAsumido;

        return <Modal isOpen={true} className="wide-dialog">
            <ModalHeader close={<button className="btn-close" onClick={() => setModalPurgaAbierto(false)}>&nbsp;</button>}>
                Purgar logs antiguos
            </ModalHeader>

            <ModalBody>
                <p className="text-muted">
                    Borra los ficheros escritos antes de la fecha indicada. No se puede deshacer, así que
                    primero hay que comprobar a cuántos afecta.
                </p>

                <Row className="align-items-end">
                    <Col md={6}>
                        <label className="form-label" htmlFor="fechaCortePurga">Conservar desde</label>
                        <Input type="date" id="fechaCortePurga" value={fechaCorte}
                            onChange={(e) => {
                                setFechaCorte(e.target.value);
                                setSimulacion(null);
                                setBorradoAsumido(false);
                            }} />
                    </Col>
                    <Col md={6}>
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="purgarSoloEstaCarpeta"
                                checked={purgarSoloEstaCarpeta}
                                disabled={!listado?.ruta}
                                onChange={(e) => {
                                    setPurgarSoloEstaCarpeta(e.target.checked);
                                    setSimulacion(null);
                                    setBorradoAsumido(false);
                                }} />
                            <label className="form-check-label" htmlFor="purgarSoloEstaCarpeta">
                                Solo esta carpeta{listado?.ruta ? ` (${listado.ruta})` : ""}
                            </label>
                        </div>
                        <div className="form-text">
                            {purgarSoloEstaCarpeta && listado?.ruta
                                ? "Se purgará solo esa carpeta y las que cuelgan de ella."
                                : "Se purgarán todas las editoriales."}
                        </div>
                    </Col>
                </Row>

                {simulacion && <div className="mt-3">{renderResultadoPurga(simulacion, true)}</div>}
                {resultadoPurga && <div className="mt-3">{renderResultadoPurga(resultadoPurga, false)}</div>}

                {simulacion != null && !simulacion.aviso && simulacion.ficherosEliminados > 0 &&
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="borradoAsumido"
                            checked={borradoAsumido}
                            onChange={(e) => setBorradoAsumido(e.target.checked)} />
                        <label className="form-check-label" htmlFor="borradoAsumido">
                            Entiendo que el borrado es definitivo.
                        </label>
                    </div>}
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" disabled={!fechaCorte} onClick={simularPurga}>
                    Comprobar
                </Button>
                <Button color="danger" disabled={!puedeBorrar} onClick={purgar}>
                    {puedeBorrar
                        ? `Borrar ${simulacion.ficherosEliminados} ficheros`
                        : "Borrar"}
                </Button>
                <Button color="link" onClick={() => setModalPurgaAbierto(false)}>
                    Cerrar
                </Button>
            </ModalFooter>
        </Modal>;
    };

    return <React.Fragment>
        {cargando && <Loading />}
        {modalPurgaAbierto && renderModalPurga()}
        {carpetaABorrar != null && renderModalCarpeta()}

        <h2><strong>Logs de proveedores</strong></h2>
        <p>
            Peticiones enviadas a las editoriales y respuestas recibidas en cada acceso a un contenido
            digital. Se guardan en el servidor de la API, agrupadas por editorial y fecha.
        </p>
        <div className="d-flex justify-content-between align-items-center mb-3">
            {renderMigas()}
            <Button color="danger" outline size="sm" onClick={abrirPurga}>
                Purgar logs antiguos
            </Button>
        </div>

        {listado?.aviso && <div className="alert alert-info">{listado.aviso}</div>}

        {resultadoCarpeta &&
            <div className={resultadoCarpeta.aviso ? "alert alert-warning" : "alert alert-success"}>
                {resultadoCarpeta.aviso
                    // Un borrado a medias también deja cifras: decir solo "no se ha podido borrar del todo"
                    // esconde que casi todo sí se fue.
                    ? <>{resultadoCarpeta.aviso}
                        {resultadoCarpeta.ficherosEliminados > 0 &&
                            <> Se han borrado {resultadoCarpeta.ficherosEliminados} ficheros
                                {" "}({formatearTamannoTotal(resultadoCarpeta.bytesLiberados)}).</>}</>
                    : <>Borrada <strong>{resultadoCarpeta.ruta}</strong>:
                        {" "}{resultadoCarpeta.ficherosEliminados} ficheros
                        {" "}({formatearTamannoTotal(resultadoCarpeta.bytesLiberados)})
                        {/* El recuento incluye la propia carpeta, así que las subcarpetas son una menos. */}
                        {resultadoCarpeta.carpetasEliminadas > 1 &&
                            <> y {resultadoCarpeta.carpetasEliminadas - 1} subcarpetas</>}.</>}
            </div>}

        <Row>
            <Col md={ficheroVisto ? 6 : 12}>
                {listado != null && listado.carpetas.length > 0 &&
                    <ul className="list-unstyled">
                        {listado.carpetas.map(carpeta =>
                            <li key={carpeta.ruta} className="py-1">
                                <a href="#" onClick={(e) => { e.preventDefault(); navegarA(carpeta.ruta); }}>
                                    📁 {carpeta.nombre}
                                </a>
                                <Button color="link" size="sm" className="text-danger py-0"
                                    title="Borrar esta carpeta con todo su contenido"
                                    onClick={() => pedirBorradoCarpeta(carpeta)}>
                                    🗑
                                </Button>
                            </li>
                        )}
                    </ul>}

                {listado != null && ficherosOrdenados.length > 0 && <>
                    {listado.hayMasFicheros &&
                        <div className="alert alert-info">
                            La carpeta tiene {listado.totalFicheros} ficheros; se muestran los
                            {" "}{listado.ficheros.length} más recientes.
                        </div>}

                    <Table striped responsive size="sm">
                        <thead>
                            <tr>
                                {renderCabecera("nombre", "Fichero")}
                                {renderCabecera("tipo", "Tipo")}
                                {renderCabecera("fecha", "Fecha (UTC)")}
                                {renderCabecera("tamanno", "Tamaño", true)}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {ficherosDeLaPagina.map((fichero: LogFicheroListItem) =>
                                <tr key={fichero.ruta}>
                                    <td className="text-break">{fichero.nombre}</td>
                                    <td>{renderTipo(fichero.tipo)}</td>
                                    <td>{fichero.fechaModificacion.toLocaleString()}</td>
                                    <td className="text-end">{formatearTamanno(fichero.tamanno)}</td>
                                    <td className="text-end text-nowrap">
                                        <Button color="link" size="sm" onClick={() => verFichero(fichero.ruta)}>Ver</Button>
                                        <Button color="link" size="sm" onClick={() => descargarFichero(fichero.ruta)}>Descargar</Button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <Pager
                        paginatedList={{
                            totalCount: ficherosOrdenados.length,
                            resultsPerPage: ficherosPorPagina,
                            currentPage: pagina,
                            items: ficherosDeLaPagina
                        }}
                        pageSize={ficherosPorPagina}
                        onClick={(nuevaPagina) => setPagina(nuevaPagina)}
                        onPageSizeChange={(nuevoTamanno) => {
                            setFicherosPorPagina(nuevoTamanno);
                            setPagina(0);
                        }}
                    />
                </>}

                {listado != null && listado.aviso == null
                    && listado.carpetas.length === 0 && ficherosOrdenados.length === 0 &&
                    <p className="text-muted">La carpeta está vacía.</p>}
            </Col>

            {ficheroVisto &&
                <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="text-break">{ficheroVisto.nombre}</h5>
                        <Button close onClick={() => setFicheroVisto(null)} />
                    </div>
                    {ficheroVisto.recortado &&
                        <div className="alert alert-info">
                            El fichero es demasiado grande para verlo entero aquí; descárgalo si necesitas el
                            resto.
                        </div>}
                    <pre className="border p-2" style={{ maxHeight: "60vh", overflow: "auto", whiteSpace: "pre-wrap" }}>
                        {ficheroVisto.contenido}
                    </pre>
                    <Button color="secondary" size="sm" onClick={() => descargarFichero(ficheroVisto.ruta)}>
                        Descargar
                    </Button>
                </Col>}
        </Row>
    </React.Fragment>;
};
